import { useEffect, useRef } from 'react';

/**
 * Custom hook to track scroll depth and log events at specified milestones
 * @param {string} pathname - Current URL pathname to reset tracking on route changes
 * @param {Function} LOG_GA_EVENT - Function to log events to Google Analytics
 */
const useScrollDepth = (pathname, LOG_GA_EVENT) => {
  // Track which milestones have been reached for the current page
  const milestonesReached = useRef({
    25: false,
    50: false,
    75: false,
    90: false,
  });

  // Store the debounce timer
  const debounceTimer = useRef(null);

  // Store LOG_GA_EVENT in a ref to avoid re-initializing on every render
  const logEventRef = useRef(LOG_GA_EVENT);

  // Update the ref when LOG_GA_EVENT changes
  logEventRef.current = LOG_GA_EVENT;

  useEffect(() => {
    // Reset milestones when pathname changes
    milestonesReached.current = {
      25: false,
      50: false,
      75: false,
      90: false,
    };

    // Find the scrollable element - could be window or .content-wrapper
    const contentWrapper = document.querySelector('.content-wrapper');
    let scrollElement = window;
    let useContentWrapper = false;

    // Check if content-wrapper is the scrollable element
    if (contentWrapper) {
      const hasVerticalScroll = contentWrapper.scrollHeight > contentWrapper.clientHeight;
      if (hasVerticalScroll) {
        scrollElement = contentWrapper;
        useContentWrapper = true;
      }
    }

    const calculateScrollPercentage = () => {
      let scrollTop, scrollHeight, clientHeight;

      if (useContentWrapper) {
        // Scrolling on content-wrapper
        scrollTop = contentWrapper.scrollTop;
        scrollHeight = contentWrapper.scrollHeight;
        clientHeight = contentWrapper.clientHeight;
      } else {
        // Scrolling on window
        scrollTop = window.scrollY || document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      }

      // Calculate how much of the page can be scrolled
      const scrollableHeight = scrollHeight - clientHeight;

      // Avoid division by zero for pages shorter than viewport
      if (scrollableHeight <= 0) return 0;

      // Calculate percentage scrolled
      const scrollPercentage = (scrollTop / scrollableHeight) * 100;

      return scrollPercentage;
    };

    const handleScroll = () => {
      // Debounce scroll events for performance
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        const scrollPercentage = calculateScrollPercentage();

        // Check each milestone and fire event if reached and not already logged
        // Check all milestones independently so if user scrolls past multiple at once, all fire
        if (scrollPercentage >= 25 && !milestonesReached.current[25]) {
          milestonesReached.current[25] = true;
          console.log('LOGGING SCROLL DEPTH 25');
          logEventRef.current('scroll_depth_25');
        }
        if (scrollPercentage >= 50 && !milestonesReached.current[50]) {
          milestonesReached.current[50] = true;
          console.log('LOGGING SCROLL DEPTH 50');
          logEventRef.current('scroll_depth_50');
        }
        if (scrollPercentage >= 75 && !milestonesReached.current[75]) {
          milestonesReached.current[75] = true;
          console.log('LOGGING SCROLL DEPTH 75');
          logEventRef.current('scroll_depth_75');
        }
        if (scrollPercentage >= 90 && !milestonesReached.current[90]) {
          milestonesReached.current[90] = true;
          console.log('LOGGING SCROLL DEPTH 90');
          logEventRef.current('scroll_depth_90');
        }
      }, 250); // 250ms debounce
    };

    // Add scroll event listener to the appropriate element
    scrollElement.addEventListener('scroll', handleScroll);

    // Cleanup function
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [pathname]); // Re-run effect only when pathname changes
};

export default useScrollDepth;
